import { useCallback, useState } from "react";
import {
  CalendarClock,
  ChevronDown,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { useNotify } from "ra-core";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { streamSkillRun } from "@/lib/agenticClient";
import { useAuthUser } from "@/hooks/useAuthUser";
import { getSupabaseClient } from "../providers/supabase/supabase";

// Sprint 10.x — Outils IA · Briefs des visios du jour.
// Pour chaque RDV calendrier des 24h à venir, génère 1 brief (read-only,
// pas d'approval). Théo lit et entre en visio préparé.

type Brief = {
  appointment_id: number;
  title: string;
  start_at: string;
  contact_id: number | null;
  contact_name: string | null;
  summary: string;
  open_deals: Array<{
    id: number;
    name: string;
    stage: string;
    amount: number | null;
  }>;
  last_email_excerpt: string | null;
  last_recording_summary: string | null;
  open_tasks: string[];
  suggested_agenda: string[];
};

export const MeetingBriefsTodayCard = () => {
  const notify = useNotify();
  const user = useAuthUser();
  const [runState, setRunState] = useState<
    "idle" | "running" | "done" | "error"
  >("idle");
  const [briefs, setBriefs] = useState<Brief[]>([]);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [runError, setRunError] = useState<string | null>(null);

  const launch = useCallback(async () => {
    setRunState("running");
    setRunError(null);
    setBriefs([]);
    try {
      // Résoudre le sales_id depuis l'auth user (mapping sales.user_id).
      const supabase = getSupabaseClient();
      const { data: salesRow, error: sErr } = await supabase
        .from("sales")
        .select("id")
        .eq("user_id", user?.id ?? "")
        .maybeSingle();
      if (sErr) throw sErr;
      if (!salesRow?.id) {
        throw new Error("Compte sales introuvable pour cet utilisateur.");
      }
      const today = new Date().toISOString().slice(0, 10);
      for await (const ev of streamSkillRun("prepare_meeting_brief", {
        sales_id: (salesRow as { id: number }).id,
        date: today,
      })) {
        if (ev.event === "run.done") {
          const out = ev.data.output as { briefs?: Brief[] };
          setBriefs(out?.briefs ?? []);
          setRunState("done");
          notify(`${out?.briefs?.length ?? 0} brief(s) prêt(s)`, {
            type: "success",
          });
        }
        if (ev.event === "run.error") throw new Error(ev.data.error);
      }
    } catch (e) {
      setRunError(e instanceof Error ? e.message : String(e));
      setRunState("error");
    }
  }, [user?.id, notify]);

  const toggle = (id: number) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <Card>
      <CardContent className="py-4 md:py-5">
        <div className="flex items-center gap-2 mb-2">
          <CalendarClock className="size-4 text-fabrik-purple" />
          <p className="text-[13px] font-semibold text-foreground">
            Briefs des visios du jour
          </p>
          {briefs.length > 0 && (
            <span className="text-[11px] text-muted-foreground">
              ({briefs.length})
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Pour chaque RDV du jour, contexte contact + deals + dernier échange +
          agenda suggéré. Lecture seule, pas de validation requise.
        </p>

        {runState === "idle" && (
          <Button size="sm" onClick={launch} className="w-full">
            Générer les briefs du jour
          </Button>
        )}
        {runState === "running" && (
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground py-2">
            <Loader2 className="size-3.5 animate-spin" />
            Préparation…
          </div>
        )}
        {runState === "error" && (
          <div className="text-xs text-destructive py-2">
            Erreur : {runError}
            <Button size="sm" variant="ghost" className="ml-2" onClick={launch}>
              Réessayer
            </Button>
          </div>
        )}
        {runState === "done" && briefs.length === 0 && (
          <p className="text-xs text-muted-foreground py-1">
            Aucun RDV calendrier prévu aujourd'hui.
          </p>
        )}

        {briefs.length > 0 && (
          <div className="mt-3 space-y-2">
            {briefs.map((b) => {
              const isOpen = !!expanded[b.appointment_id];
              const hour = new Date(b.start_at).toLocaleTimeString("fr-FR", {
                hour: "2-digit",
                minute: "2-digit",
              });
              return (
                <div
                  key={b.appointment_id}
                  className="rounded border border-fabrik-purple/30 bg-fabrik-purple/5 p-2"
                >
                  <button
                    type="button"
                    onClick={() => toggle(b.appointment_id)}
                    className="w-full text-left flex items-start gap-1.5"
                  >
                    {isOpen ? (
                      <ChevronDown className="size-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="size-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">
                        {hour} · {b.title}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {b.contact_name ?? "Sans contact lié"}
                      </div>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="mt-2 pt-2 border-t border-fabrik-purple/20 text-xs space-y-2">
                      <div>
                        <div className="text-[10.5px] uppercase tracking-wider text-fabrik-text-3 mb-0.5">
                          Contexte
                        </div>
                        <p className="whitespace-pre-wrap leading-relaxed">
                          {b.summary}
                        </p>
                      </div>
                      {b.open_deals.length > 0 && (
                        <div>
                          <div className="text-[10.5px] uppercase tracking-wider text-fabrik-text-3 mb-0.5">
                            Deals ouverts
                          </div>
                          <ul className="space-y-0.5">
                            {b.open_deals.map((d) => (
                              <li key={d.id} className="text-xs">
                                · {d.name} —{" "}
                                <span className="text-muted-foreground">
                                  {d.stage}
                                  {d.amount !== null ? ` · ${d.amount}€` : ""}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {b.last_email_excerpt && (
                        <div>
                          <div className="text-[10.5px] uppercase tracking-wider text-fabrik-text-3 mb-0.5">
                            Dernier mail
                          </div>
                          <p className="text-xs text-muted-foreground italic line-clamp-3">
                            {b.last_email_excerpt}
                          </p>
                        </div>
                      )}
                      {b.last_recording_summary && (
                        <div>
                          <div className="text-[10.5px] uppercase tracking-wider text-fabrik-text-3 mb-0.5">
                            Dernier appel
                          </div>
                          <p className="text-xs text-muted-foreground italic line-clamp-3">
                            {b.last_recording_summary}
                          </p>
                        </div>
                      )}
                      {b.open_tasks.length > 0 && (
                        <div>
                          <div className="text-[10.5px] uppercase tracking-wider text-fabrik-text-3 mb-0.5">
                            Tâches ouvertes
                          </div>
                          <ul className="space-y-0.5">
                            {b.open_tasks.map((t, i) => (
                              <li key={i} className="text-xs">
                                · {t}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {b.suggested_agenda.length > 0 && (
                        <div>
                          <div className="text-[10.5px] uppercase tracking-wider text-fabrik-text-3 mb-0.5">
                            Agenda suggéré
                          </div>
                          <ul className="space-y-0.5">
                            {b.suggested_agenda.map((a, i) => (
                              <li key={i} className="text-xs">
                                · {a}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
